import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { hasPermission } from "@/lib/permissions";
import { Navbar } from "@/components/Navbar";
import { PlaybookRunCard } from "@/components/PlaybookRunCard";
import { IntroNote } from "@/components/IntroNote";
import { toast } from "sonner";
import { Bell, Hand, Zap, Workflow } from "lucide-react";

const KIND_ICON = { manual: Hand, notify: Bell, auto_remediation: Zap };

export default function Playbooks() {
  const { user } = useAuth();
  const canReadRisks = hasPermission(user, "risk.read");
  const [playbooks, setPlaybooks] = useState([]);
  const [runs, setRuns] = useState([]);
  const [riskTitles, setRiskTitles] = useState({});

  const pbMap = Object.fromEntries(playbooks.map((p) => [p.playbook_id, p]));

  const loadRuns = useCallback(() => {
    return api
      .get(`/playbook-runs?assigned_to_user_id=${user.user_id}`)
      .then(({ data }) => setRuns(data))
      .catch(() => {});
  }, [user.user_id]);

  useEffect(() => {
    api.get("/playbooks").then(({ data }) => setPlaybooks(data)).catch(() => {});
    loadRuns();
    if (canReadRisks) {
      api
        .get("/risks")
        .then(({ data }) =>
          setRiskTitles(Object.fromEntries(data.items.map((r) => [r.risk_id, r.title])))
        )
        .catch(() => {});
    }
  }, [loadRuns, canReadRisks]);

  const patchStep = async (runId, stepNo, state) => {
    try {
      await api.patch(`/playbook-runs/${runId}/step/${stepNo}`, { state });
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update step");
    }
  };

  const abort = async (runId) => {
    try {
      await api.post(`/playbook-runs/${runId}/abort`);
      toast.success("Run aborted");
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to abort run");
    }
  };

  const requestRemediation = async (runId, stepNo) => {
    try {
      await api.post("/remediation-actions", { run_id: runId, step_no: stepNo });
      toast.success("Remediation requested — awaiting approval");
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to request remediation");
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-12" data-testid="playbooks-page">
        <p className="ek-eyebrow">Risk-to-Action Engine</p>
        <h1 className="ek-heading mt-2 text-4xl sm:text-5xl">Playbooks</h1>
        <p className="mt-3 text-base text-ek-ink/65">
          Response playbooks triggered by risk thresholds, assigned to named owners.
        </p>

        <IntroNote icon={Workflow} title="What you're looking at" testid="playbooks-intro" className="mt-6">
          The bridge from detection to response. The catalog holds reusable playbooks that fire when a risk
          crosses its threshold; "My assigned runs" are the live responses you personally own — work each step
          to completion, and request automated remediation where a step allows it.
        </IntroNote>

        {/* Catalog */}
        <h2 className="ek-eyebrow mt-10">Catalog</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
          {playbooks.map((pb) => (
            <div
              key={pb.playbook_id}
              data-testid="playbook-card"
              className="rounded-md border border-black/5 bg-white p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-md bg-ek-purple/10 text-ek-purple">
                  <Workflow size={20} />
                </div>
                <span
                  className={`px-2 py-0.5 text-[0.6rem] font-semibold uppercase ${pb.origin === "org" ? "bg-ek-purple/10 text-ek-purple" : "bg-ek-canvas text-ek-ink/65"}`}
                  data-testid={`playbook-origin-${pb.origin || "library"}`}
                >
                  {pb.origin === "org" ? "Org" : "Template"}
                </span>
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-ek-ink">{pb.title}</h3>
              <p className="mt-1 text-xs text-ek-ink/65">
                Triggers when residual ≥ {pb.trigger.min_residual_score}
              </p>
              <div className="mt-4 space-y-2">
                {pb.steps.map((s) => {
                  const KindIcon = KIND_ICON[s.kind] || Hand;
                  return (
                    <div key={s.step_no} className="flex items-center gap-2 text-sm text-ek-ink/70">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ek-canvas text-[10px] font-semibold text-ek-ink/65">
                        {s.step_no}
                      </span>
                      <span className="flex-1">{s.label}</span>
                      <KindIcon size={12} className="text-ek-ink/30" />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* My runs */}
        <h2 className="ek-eyebrow mt-12">My assigned runs</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="my-runs">
          {runs.length === 0 ? (
            <p className="text-sm text-ek-ink/30">No runs assigned to you yet.</p>
          ) : (
            runs.map((run) => (
              <PlaybookRunCard
                key={run.run_id}
                run={run}
                playbook={pbMap[run.playbook_id]}
                user={user}
                riskTitle={riskTitles[run.risk_id]}
                onPatchStep={patchStep}
                onAbort={abort}
                onRequestRemediation={requestRemediation}
              />
            ))
          )}
        </div>
      </main>
    </div>
  );
}
